import "./style.scss";

import { Tooltip } from "@mui/material";
import BasicTable from "components/shared/BasicTable";
import type { BasicTableColumn } from "components/shared/BasicTable";
import PeriodName from "components/shared/PeriodName";
import { formatNumber } from "helpers/utils";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { IAdvanceSlice } from "store/advancePaymentSlice";
import type { DTOTermPeriodData, ITerm } from "store/managementStore";
import type { TPeriod } from "store/periodStore";
import { useAppDispatch, useAppSelector } from "store/store";
import { getTPDLinkedToTerm } from "store/termStore";
import { PeriodStatus } from "store/types";

interface IBalanceTable {
    term: ITerm | undefined;
    termAdvancePayments: IAdvanceSlice[] | undefined;
}

/**
 * Table which recaps balance per period and changes made to it.
 * Uses BasicTable for consistent styling with other recap tables.
 */
const BalanceTable = ({ term, termAdvancePayments }: IBalanceTable) => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const { termTpd } = useAppSelector((state) => state.term);
    const { periods } = useAppSelector((state) => state.period);

    const [dataLoading, setDataLoading] = useState(true);

    const data = useMemo(() => {
        let rows = [...termTpd];
        if (term?.initialAdvance) {
            const initialAdvanceLine = {
                periodId: -1,
                balance: term.initialAdvance,
                comment: null,
            } as DTOTermPeriodData;
            rows = [initialAdvanceLine, ...rows];
        }
        return rows.reverse();
    }, [termTpd, term?.initialAdvance]);

    const originalPeriod: TPeriod | undefined = useMemo(() => {
        if (!term || !periods) return;
        return periods.find((item) => term?.originalPeriod === item.id);
    }, [term, periods]);

    const fetchTermTpd = async (termId: number) => {
        setDataLoading(true);
        try {
            const periodStatus = [PeriodStatus.CLOSED, PeriodStatus.CURRENTLY_OPEN];
            await dispatch(getTPDLinkedToTerm({ termId, periodStatus }));
        } finally {
            setDataLoading(false);
        }
    };

    useEffect(() => {
        if (!term?.id) return;
        fetchTermTpd(term.id);
    }, [term]);

    const renderDetail = (tpd: DTOTermPeriodData) => {
        if (originalPeriod && tpd.periodId === originalPeriod.id) {
            return <span>{t("BalanceUpdate.BalanceTable.InitialBalance")}</span>;
        }
        if (tpd.periodId === -1) {
            return <span>{t("BalanceUpdate.BalanceTable.InitialAdvance")}</span>;
        }
        if (!termTpd.length) return <span>-</span>;

        const currentTPDIndex = termTpd.findIndex((item) => item.id === tpd.id);
        const previousTPD = currentTPDIndex > 0 ? termTpd[currentTPDIndex - 1] : undefined;
        let previousBalance = 0;
        if (previousTPD) {
            previousBalance = previousTPD.underTheMinimum ? previousTPD.grossDuty : previousTPD.nextBalance;
        }

        const linkedAdvance = termAdvancePayments?.length
            ? termAdvancePayments.find((item) => item.connectedTPD === tpd.id)
            : undefined;

        return (
            <div className="balance-detail-column">
                {previousTPD ? (
                    <span>
                        {t("BalanceUpdate.BalanceTable.OldPeriodBalance", {
                            value: formatNumber(previousBalance / 100, { isCurrency: true }),
                        })}
                    </span>
                ) : null}
                {tpd.balanceModified && linkedAdvance ? (
                    <>
                        <span>
                            {t("BalanceUpdate.BalanceTable.NewAdvance", {
                                value: formatNumber(linkedAdvance.totalAmount / 100, { isCurrency: true }),
                            })}
                        </span>
                        {linkedAdvance.slices?.map((slice, idx) => (
                            <span key={`slice-${slice.id}`}>
                                {t("BalanceUpdate.BalanceTable.Slice", {
                                    number: idx + 1,
                                    value: formatNumber(slice.amount / 100, { isCurrency: true }),
                                })}
                            </span>
                        ))}
                    </>
                ) : tpd.balanceModified ? (
                    <span>
                        {t("BalanceUpdate.BalanceTable.NewBalance", {
                            value: formatNumber(tpd.balance / 100, { isCurrency: true }),
                        })}
                    </span>
                ) : null}
            </div>
        );
    };

    const renderComment = (tpd: DTOTermPeriodData) => {
        if (!tpd.balanceModified) {
            return <span>-</span>;
        }

        const linkedAdvance = termAdvancePayments?.length
            ? termAdvancePayments.find((item) => item.connectedTPD === tpd.id)
            : undefined;

        if (linkedAdvance) {
            return (
                <div className="balance-comment-column">
                    <span style={{ display: "block", height: "18px" }} />
                    {linkedAdvance.slices?.map((slice) => (
                        <Tooltip key={`slice-comment-${slice.id}`} title={slice.comment} arrow>
                            <span style={{ display: "block" }}>{slice.comment || "-"}</span>
                        </Tooltip>
                    ))}
                </div>
            );
        }

        return (
            <Tooltip title={tpd.comment} arrow>
                <div className="balance-comment-column" style={{ cursor: "pointer" }}>
                    {tpd.comment || "-"}
                </div>
            </Tooltip>
        );
    };

    const columns = useMemo<BasicTableColumn<DTOTermPeriodData>[]>(
        () => [
            {
                header: t("BPStatement.Period"),
                render: (row) => {
                    if (row.periodId === -1) return "-";
                    const period = periods.find((item) => item.id === row.periodId);
                    return period ? <PeriodName period={period} /> : "-";
                },
            },
            {
                header: t("AdvancePayment.TotalAmount"),
                align: "right",
                render: (row) => formatNumber(row.balance / 100, { isCurrency: true }),
            },
            {
                header: t("BalanceUpdate.BalanceTable.Detail"),
                render: (row) => renderDetail(row),
            },
            {
                header: t("BalanceUpdate.BalanceTable.Comment"),
                render: (row) => renderComment(row),
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [t, periods, termTpd, termAdvancePayments, originalPeriod],
    );

    return (
        <div className="balance-table-container">
            <BasicTable<DTOTermPeriodData>
                caption={t("BalanceUpdate.BalanceTable.Title")}
                columns={columns}
                rows={data}
                isLoading={dataLoading}
                skeletonRows={3}
                maxRows={3}
                showAllLabel={t("BalanceUpdate.BalanceTable.ShowAll")}
                hideLabel={t("BalanceUpdate.BalanceTable.Hide")}
            />
        </div>
    );
};

export default BalanceTable;
